/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CosmosImageFragment } from "@cosmos/hygraph-schema";
import { CSSProperties } from "react";
import { Animate, ButtonList, CosmosButtonProps, CosmosImage, CosmosText, Wrap } from "@components/elements";
import { useDisabledHygraphImageLoader } from "@components/hooks/imageloaders.hooks";
import { CosmosStackProps } from "@components/types";
import { css } from "@panda/css";
import { Stack } from "@panda/jsx";
import { token } from "@panda/tokens";
import { TitleDescStack, TitleDescStackProps } from "../title-desc-stack/TitleDescStack.molecule";
import { useIsRtl } from "@components/hooks";

export interface LogoTitleDescButtonStackProps {
	animate?: boolean;
	buttons?: CosmosButtonProps[] | null;
	image?: Omit<CosmosImageFragment, "id"> | null;
	imageLabel?: string | null;
	eyebrow?: string | null;
	stack?: CosmosStackProps | null;
	titleDescStack?: TitleDescStackProps | null;
}

interface CssVariables extends CSSProperties {
	"--gap"?: string;
}

export const LogoTitleDescButtonStack = ({
	animate = false,
	buttons = [],
	image,
	imageLabel,
	eyebrow,
	stack,
	titleDescStack,
}: LogoTitleDescButtonStackProps): React.JSX.Element => {
	const spacingToken = stack?.gap ? token(`spacing.${stack?.gap}`) : token("spacing.eds.dimension.24");
	const style: CssVariables = { "--gap": spacingToken };
	const disabledHygraphImageLoader = useDisabledHygraphImageLoader();
	const isRtl = useIsRtl();

	const titleStackEl = <TitleDescStack animate={animate} {...titleDescStack}></TitleDescStack>;

	return (
		<Stack
			{...stack}
			className={`logo-title-desc-button-stack ${css({
				gap: "var(--gap)",
				"& img": {
					objectPosition: isRtl ? "right bottom" : "left bottom",
				},
			})}`}
			direction={stack?.direction || "column"}
			style={style}
		>
			{image && image.url ? (
				<Wrap condition={animate} wrap={(children) => <Animate type="slide">{children}</Animate>}>
					<div className="logo">
						<CosmosImage
							alt={imageLabel || image?.altText || ""}
							src={image.url}
							width={image.width || undefined}
							height={image.height || undefined}
							style={{ objectFit: "contain" }}
							unoptimized
							/** cant resize svgs */
							loader={disabledHygraphImageLoader}
						/>
					</div>
				</Wrap>
			) : (
				""
			)}

			{eyebrow ? (
				<Stack
					{...stack}
					className={css({
						gap: "var(--gap)",
					})}
					direction={stack?.direction || "column"}
					style={style}
				>
					<Wrap condition={animate} wrap={(children) => <Animate type="slide">{children}</Animate>}>
						<CosmosText variant="eyebrowLG" text={eyebrow} />
					</Wrap>
					{titleStackEl}
				</Stack>
			) : (
				titleStackEl
			)}

			<ButtonList animate={animate} buttons={buttons} />
		</Stack>
	);
};
